/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 유틸성 함수를 정의한다.
 *
 * @file /src/utils/Utils.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 1. 7.
 */
import { VERSION } from '@base/Aui';

export class Utils {
    static loader: Map<string, Promise<boolean>> = new Map();

    /**
     * 자바스크립트파일을 비동기로 로드한다.
     *
     * @param url - 로드할 자바스크립트경로
     * @returns 성공여부
     */
    static loadJs(url: string): Promise<boolean> {
        /**
         * 이미 호출중인 로더가 있다면, 해당 로더를 반환한다.
         */
        if (Utils.loader.has(url) == true) {
            return Utils.loader.get(url);
        }

        const existingScript = document.querySelector(`script[src="${url}"]`);
        if (existingScript) {
            return Promise.resolve(true);
        }

        const promise = new Promise<boolean>((resolve) => {
            const script = document.createElement('script');
            script.src = url;
            script.async = true;
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.head.appendChild(script);
        }).finally(() => {
            Utils.loader.delete(url);
        });

        Utils.loader.set(url, promise);

        return promise;
    }

    /**
     * CSS 파일을 비동기로 로드한다.
     *
     * @param url - 로드할 CSS 경로
     * @returns 성공 여부
     */
    static loadCss(url: string): Promise<boolean> {
        /**
         * 이미 호출중인 로더가 있다면, 해당 로더를 반환한다.
         */
        if (Utils.loader.has(url) == true) {
            return Utils.loader.get(url);
        }

        const existingLink = document.querySelector(`link[rel="stylesheet"][href="${url}"]`);
        if (existingLink) {
            return Promise.resolve(true);
        }

        const promise = new Promise<boolean>((resolve) => {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = url;
            link.onload = () => resolve(true);
            link.onerror = () => resolve(false);
            document.head.appendChild(link);
        }).finally(() => {
            Utils.loader.delete(url);
        });

        Utils.loader.set(url, promise);

        return promise;
    }

    /**
     * 지정된 시간만큼 자바스크립트를 지연시킨다.
     *
     * @param ms - 지연될 ms
     */
    static async sleep(ms: number): Promise<void> {
        return new Promise((r) => setTimeout(r, ms));
    }

    /**
     * 세션 스토리지의 데이터를 처리한다.
     *
     * @param key - 데이터키
     * @param value - 저장할 데이터 (undefined 인 경우 저장된 데이터를 가져온다.)
     * @returns 데이터를 가져올 경우 해당 데이터값
     */
    static session(key: string, value: any = undefined): any {
        const session = window.sessionStorage?.getItem('Aui-Session') ?? null;
        let datas = session !== null ? JSON.parse(session) : {};

        /**
         * 세션데이터의 버전이 현재 Aui 버전과 다를 경우, 무시한다.
         */
        if (datas.VERSION !== VERSION) {
            datas = { VERSION: VERSION };
        }

        if (value === undefined) {
            return datas[key] ?? null;
        } else {
            datas[key] = value;
            window.sessionStorage?.setItem('Aui-Session', JSON.stringify(datas));
        }
    }

    /**
     * 로컬 스토리지의 데이터를 처리한다.
     *
     * @param key - 데이터키
     * @param value - 저장할 데이터 (undefined 인 경우 저장된 데이터를 가져온다.)
     * @returns 데이터를 가져올 경우 해당 데이터값
     */
    static storage(key: string, value: any = undefined): any {
        const storage = window.localStorage?.getItem('Aui-Storage') ?? null;
        let datas = storage !== null ? JSON.parse(storage) : {};

        /**
         * 세션데이터의 버전이 현재 Aui 버전과 다를 경우, 무시한다.
         */
        if (datas.VERSION !== VERSION) {
            datas = { VERSION: VERSION };
        }

        if (value === undefined) {
            return datas[key] ?? null;
        } else {
            datas[key] = value;
            window.localStorage?.setItem('Aui-Storage', JSON.stringify(datas));
        }
    }
}
